import { useEffect, useMemo, useRef, useState, type FormEvent, type Ref } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Loader2, CalendarX, Video } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  isAppointmentEditable,
  useAppointment,
  useCancelAppointment,
  useCompleteAppointment,
  useMarkNoShowAppointment,
  useCreateAppointment,
  useCreateRecurringAppointment,
  useRescheduleAppointment,
  useUpdateAppointmentNotes,
  fetchUpcomingSeriesMembers,
  useDeleteAppointmentSeries,
  useUpdateAppointmentSeries,
  type CreateAppointmentInput,
} from '@/hooks/useAppointments';
import {
  buildAppointmentInvoiceHref,
  useAppointmentInvoiceLink,
} from '@/hooks/useAppointmentInvoicing';
import { useCreateService, useServicesList } from '@/hooks/useServices';
import { useCalendarBlockOverlaps } from '@/hooks/useCalendarBlockOverlaps';
import { useBlockClinicianOccupants, useSlotOccupants } from '@/hooks/useSlotOccupants';
import { useAppointmentPractitionerRows } from '@/hooks/useAppointmentPractitioners';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { usePatientStaffAssignments } from '@/hooks/usePatientStaffAssignments';
import { useAuth } from '@/lib/auth';
import {
  filterPractitionerMembers,
  preselectedAppointmentPractitionerIds,
  withStickySelectedMembers,
} from '@/lib/practitionerRole';
import { suggestedLinkedPractitionerIds } from '@/lib/practitionerSuggestions';
import { usePatientPicker, type PatientPickerItem } from '@/hooks/usePatientPicker';
import { usePatient } from '@/hooks/usePatients';
import {
  formatPatientTelephoneNumbers,
  primaryTelephoneNumber,
  readTelephoneSubscribers,
} from '@/lib/patientStamkortFields';
import { Avatar } from '@/components/ui/Avatar';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { Input } from '@/components/ui/Input';
import { TimeInput } from '@/components/ui/TimeInput';
import { Select } from '@/components/ui/Select';
import { SlideOver } from '@/components/ui/SlideOver';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { Field } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { CancelAppointmentModal, type CancelScope } from './CancelAppointmentModal';
import { formatAppointmentClock } from '@/utils/appointmentTimeFormat';
import { calendarBlockOverlapLabel } from '@/utils/calendarBlockOverlap';
import { RecurrenceForm, type RecurrenceFormData } from '@/components/appointments/RecurrenceForm';
import { AvailabilityPicker } from '@/components/appointments/AvailabilityPicker';
import { BlockStaffPicker } from '@/components/appointments/BlockStaffPicker';
import { AppointmentTypeTypeahead } from '@/components/appointments/AppointmentTypeTypeahead';
import { PractitionerTypeahead } from '@/components/appointments/PractitionerTypeahead';
import {
  useAvailableResourcesForSlot,
  useResourceGroups,
  useResources,
  useServicePractitioners,
  useServiceResourceRequirements,
} from '@/hooks/useSchedulingResources';
import { useClinic } from '@/hooks/useClinicSettings';
import { useClinicHoursExceptions } from '@/hooks/useClinicHoursExceptions';
import {
  toMemberDayExceptions,
  useMemberScheduleExceptions,
} from '@/hooks/usePractitionerDayHours';
import { WORKING_HOURS_DAY_KEYS } from '@/utils/appointmentCalendarDates';
import {
  civilDateFromLocalDate,
  exceptionByDate,
  getEffectiveBookableWindows,
  isClosedHoursException,
  outsideShownCalendarHours,
  parseWeeklyHours,
  resolveClinicHoursForLocalDate,
} from '@/lib/clinicHours';
import { appointmentSaveErrorI18nKey } from '@/utils/appointmentSaveError';
import {
  applyAppointmentCivilDate,
  isCompleteCivilDate,
  safeIsoString,
  safeLocalDateTime,
} from '@/utils/appointmentCivilDate';
import { describeSlotHint } from '@/utils/appointmentSlotHint';
import {
  appointmentEndForService,
  allDayBounds,
  blockCreateSlots,
  blockEndAfterStartChange,
  isAllDayCalendarBlock,
  blockReasonChoices,
  blockRemovalPlan,
  calendarSlideDescription,
  lockedAppointmentOpen,
  recurrenceFieldDisabled,
  resolveBlockServiceChoice,
  seriesEditTargets,
  shiftedRange,
  staffOverlapDecision,
  type BlockEditScope,
} from '@/utils/calendarTimeBlock';
import {
  blockOverlapSaveMode,
  clinicianIdsForBlockSave,
  groupBlockConflicts,
  multiBlockConflicts,
  type PractitionerBlockConflict,
} from '@/utils/multiBlockOverlap';
import { DEFAULT_BLOCKER_FORM } from '@/lib/serviceEditorDefaults';
import { zonedDateTime } from '@/lib/reporting/timezone';
import { CLINIC_TIMEZONE } from '@/utils/calendarClinicTime';
import { SERVICE_DURATION_MAX } from '@/lib/serviceDuration';
import {
  filterPractitionersForService,
  parsePractitionerEligibility,
  resourceRequirementsForAppointmentService,
} from '@/lib/serviceBookingEligibility';
import { shouldSendStaffAppointmentEmail } from '@/lib/calendarPaymentChoice';
import {
  sendStaffAppointmentConfirmation,
  sendStaffAppointmentEmail,
} from '@/lib/staffAppointmentConfirmation';
import { isPatientAppointmentType } from '@/lib/appointmentTypeCatalog';
import {
  useJoinWherebyMeeting,
  useProvisionWherebyMeeting,
  useSyncWherebyMeeting,
  useWherebyEnabled,
} from '@/hooks/useWhereby';
import { isVideoAppointment, openExternalMeeting } from '@/lib/whereby';
import {
  orderedAppointmentPractitionerIds,
  practitionerSelectionChanged,
  syncExtraAppointmentPractitioners,
} from '@/lib/appointmentExtraPractitioners';

export interface AppointmentModalProps {
  clinicId: string;
  mode: 'create' | 'edit';
  appointmentId?: string | null;
  /** Initial slot when creating (clicked time slot in the calendar). */
  initialStart?: Date | null;
  /** Optional explicit end time when creating from a drag-range selection. */
  initialEnd?: Date | null;
  initialClinicianId?: string | null;
  /** Preferred resources when creating from a resource-column slot. */
  initialResourceIds?: string[] | null;
  initialResourceId?: string | null;
  /** Pre-select patient when creating from a patient detail page. */
  initialPatientId?: string | null;
  /** Pre-check the video-meeting option (Videomøde tab). */
  initialVideoMeeting?: boolean;
  /** Open directly on Bloker tid. The panel can still switch to Aftale. */
  initialIntent?: 'appointment' | 'block';
  onClose: () => void;
}

function isoLocal(d: Date): string {
  const parts = zonedDateTime(d, CLINIC_TIMEZONE);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${parts.year}-${pad(parts.month)}-${pad(parts.day)}` +
    `T${pad(parts.hour)}:${pad(parts.minute)}`
  );
}

export function AppointmentModal(props: AppointmentModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    clinicId,
    mode,
    appointmentId,
    initialStart,
    initialEnd,
    initialClinicianId,
    initialResourceIds,
    initialResourceId,
    initialPatientId,
    initialIntent = 'appointment',
    initialVideoMeeting = false,
    onClose,
  } = props;

  const { data: existing, isLoading: loadingExisting } = useAppointment(
    mode === 'edit' ? appointmentId : null,
  );
  const practitionerRows = useAppointmentPractitionerRows(
    mode === 'edit' ? (appointmentId ?? null) : null,
  );
  const { data: initialPatient } = usePatient(
    mode === 'create' ? (initialPatientId ?? null) : null,
  );
  const { data: existingPatient } = usePatient(
    mode === 'edit' && existing ? existing.patient_id : null,
  );
  const { data: services } = useServicesList(clinicId, { includeInactive: false });
  const { data: members } = useClinicMembers(clinicId);
  const { data: roles, isSuccess: rolesReady } = useClinicRoles(clinicId);
  const { data: clinic } = useClinic(clinicId);
  const { data: wherebyEnabled } = useWherebyEnabled(clinicId);
  const provisionVideo = useProvisionWherebyMeeting(clinicId);
  const syncVideo = useSyncWherebyMeeting(clinicId);
  const joinVideo = useJoinWherebyMeeting(clinicId);

  const create = useCreateAppointment();
  const createService = useCreateService(clinicId);
  const createRecurring = useCreateRecurringAppointment();
  const reschedule = useRescheduleAppointment();
  const updateNotes = useUpdateAppointmentNotes();
  const cancel = useCancelAppointment();
  const complete = useCompleteAppointment();
  const markNoShow = useMarkNoShowAppointment();
  const deleteSeries = useDeleteAppointmentSeries();
  const updateSeries = useUpdateAppointmentSeries();

  const [patient, setPatient] = useState<PatientPickerItem | null>(null);
  const { user, status: authStatus } = useAuth();
  const staffAssignments = usePatientStaffAssignments(patient?.id);
  const [serviceId, setServiceId] = useState<string | null>(null);
  const selectedService = services?.find((s) => s.id === serviceId);
  const isBlockerService = Boolean(selectedService?.is_blocker);
  const appointmentTypes = (services ?? []).filter(isPatientAppointmentType);
  const [intent, setIntent] = useState<'appointment' | 'block'>(initialIntent);
  const blockMode = intent === 'block' || isBlockerService;
  const [blockCustomLabel, setBlockCustomLabel] = useState('');
  const [blockClinicianIds, setBlockClinicianIds] = useState<string[]>(
    initialClinicianId ? [initialClinicianId] : [],
  );
  const [allDay, setAllDay] = useState(false);
  const [untilCivil, setUntilCivil] = useState('');
  const [blockScope, setBlockScope] = useState<BlockEditScope>('single');
  // Calendar staff (any member, including Admin) is only filled in once the
  // Behandler flag is known. Bloker tid still starts from that same id.
  const [appointmentPractitionerIds, setAppointmentPractitionerIds] = useState<string[]>([]);
  const practitionerTouched = useRef(false);
  const clinicianId =
    blockMode && mode === 'create'
      ? (blockClinicianIds[0] ?? null)
      : (appointmentPractitionerIds[0] ?? null);
  const [startsAt, setStartsAt] = useState<string>('');
  const [endsAt, setEndsAt] = useState<string>('');
  const selectedStart = safeLocalDateTime(startsAt);
  const selectedCivilDate = selectedStart ? civilDateFromLocalDate(selectedStart) : null;
  const { data: hourExceptions } = useClinicHoursExceptions(
    clinicId,
    selectedCivilDate,
    selectedCivilDate,
  );
  const { data: memberExceptions } = useMemberScheduleExceptions(
    clinicId,
    selectedCivilDate,
    selectedCivilDate,
  );
  const hoursCheck = useMemo(() => {
    const start = safeLocalDateTime(startsAt);
    const end = safeLocalDateTime(endsAt);
    if (!start || !end || blockMode) {
      return { outsideOpeningHours: false, closedException: false, blockClosedDate: false };
    }
    const weeklyHours = parseWeeklyHours(clinic?.business_hours);
    const iso = civilDateFromLocalDate(start);
    const resolved = resolveClinicHoursForLocalDate({
      date: start,
      weeklyHours,
      exception: exceptionByDate(hourExceptions).get(iso) ?? null,
    });
    const closedException = isClosedHoursException(resolved);
    const originalDate = existing?.starts_at
      ? civilDateFromLocalDate(new Date(existing.starts_at))
      : null;
    const member = members?.find((item) => item.user_id === clinicianId);
    const practitionerPeriods = getEffectiveBookableWindows({
      clinicPeriods: resolved.periods,
      clinicSource: resolved.source,
      clinicExceptionType: resolved.exceptionType,
      clinicianDay: member?.working_hours[WORKING_HOURS_DAY_KEYS[start.getDay()]],
      memberExceptions: toMemberDayExceptions(memberExceptions, member?.membership_id, iso),
    });
    return {
      outsideOpeningHours: outsideShownCalendarHours(resolved, practitionerPeriods, start, end),
      closedException,
      blockClosedDate:
        closedException && (mode === 'create' || originalDate !== civilDateFromLocalDate(start)),
    };
  }, [
    startsAt,
    endsAt,
    blockMode,
    clinic?.business_hours,
    hourExceptions,
    memberExceptions,
    members,
    clinicianId,
    existing?.starts_at,
    mode,
  ]);
  const outsideOpeningHours = hoursCheck.outsideOpeningHours;
  const blockClosedDate = hoursCheck.blockClosedDate;
  const slotStart = safeLocalDateTime(startsAt);
  const slotEnd = safeLocalDateTime(endsAt);
  const calendarBlockOverlaps = useCalendarBlockOverlaps({
    clinicId,
    clinicianId,
    start: slotStart,
    end: slotEnd,
    excludeAppointmentId: mode === 'edit' ? (appointmentId ?? null) : null,
    enabled: !blockMode,
  });
  const blockOverlaps = calendarBlockOverlaps.data ?? [];
  const blockOverlapLabel =
    blockOverlaps.length > 0
      ? calendarBlockOverlapLabel(blockOverlaps, (date) =>
          formatAppointmentClock(date, i18n.language),
        )
      : '';
  const slotOccupants = useSlotOccupants({
    clinicId,
    clinicianId,
    patientId: patient?.id ?? null,
    start: slotStart,
    enabled: Boolean(slotStart && slotEnd && clinicianId),
  });
  const multiBlock = blockMode && mode === 'create' && blockClinicianIds.length > 1;
  const blockOccupants = useBlockClinicianOccupants({
    clinicId,
    clinicianIds: blockClinicianIds,
    start: slotStart,
    enabled: multiBlock && Boolean(slotStart && slotEnd),
  });
  const blockConflictNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members ?? []) map.set(member.user_id, member.name);
    return map;
  }, [members]);
  const blockConflicts = useMemo(() => {
    if (!multiBlock || !slotStart || !slotEnd) return [];
    return multiBlockConflicts({
      clinicianIds: blockClinicianIds,
      names: blockConflictNames,
      start: slotStart,
      end: slotEnd,
      occupants: blockOccupants.data ?? [],
      formatClock: (date) => formatAppointmentClock(date, i18n.language, CLINIC_TIMEZONE),
    });
  }, [
    multiBlock,
    blockClinicianIds,
    blockConflictNames,
    slotStart,
    slotEnd,
    blockOccupants.data,
    i18n.language,
  ]);
  const [blockChoice, setBlockChoice] = useState<PractitionerBlockConflict[] | null>(null);
  const slotHint = describeSlotHint({
    patientName: blockMode
      ? blockCustomLabel || selectedService?.name || 'Blokering'
      : patient
        ? `${patient.first_name} ${patient.last_name}`.trim()
        : '',
    patientId: blockMode ? null : (patient?.id ?? null),
    clinicianId,
    start: slotStart,
    end: slotEnd,
    occupants: slotOccupants.data ?? [],
    excludeAppointmentId: mode === 'edit' ? (appointmentId ?? null) : null,
    formatClock: (date) => formatAppointmentClock(date, i18n.language),
  });
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [showAvailabilityPicker, setShowAvailabilityPicker] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrenceFormData>({
    pattern: 'none',
    endType: 'count',
    endDate: null,
    count: 4,
  });
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelScope, setCancelScope] = useState<CancelScope>('single');
  const [sendPatientEmail, setSendPatientEmail] = useState(true);
  const [isVideoMeeting, setIsVideoMeeting] = useState(initialVideoMeeting);
  const [sendCancelEmail, setSendCancelEmail] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<{
    patient?: string;
    clinician?: string;
    end?: string;
  }>({});
  const clinicianFieldRef = useRef<HTMLSelectElement>(null);
  const practitionerSearchRef = useRef<HTMLInputElement>(null);
  const blockStaffSearchRef = useRef<HTMLInputElement>(null);
  const endFieldRef = useRef<HTMLSelectElement>(null);
  const patientSearchRef = useRef<HTMLInputElement>(null);
  const [selectedResourceByGroup, setSelectedResourceByGroup] = useState<Record<string, string>>(
    {},
  );

  const { data: serviceRequirementRows } = useServiceResourceRequirements(clinicId, serviceId);
  const serviceRequirements = resourceRequirementsForAppointmentService(
    serviceId,
    serviceRequirementRows,
  );
  const { data: servicePractitioners } = useServicePractitioners(clinicId, serviceId);
  const { data: resourceGroups } = useResourceGroups(clinicId);
  const { data: clinicResources } = useResources(clinicId);

  useEffect(() => {
    if (mode !== 'create' || !initialResourceIds?.length || !serviceRequirements?.length) return;
    const next: Record<string, string> = {};
    for (const requirement of serviceRequirements) {
      const match = (clinicResources ?? []).find(
        (resource) =>
          resource.resource_group_id === requirement.resource_group_id &&
          initialResourceIds.includes(resource.id),
      );
      if (match) next[requirement.resource_group_id] = match.id;
    }
    if (Object.keys(next).length === 0) return;
    setSelectedResourceByGroup((current) => ({ ...current, ...next }));
  }, [mode, initialResourceIds, serviceRequirements, clinicResources]);
  const startISOForResources = safeIsoString(safeLocalDateTime(startsAt));
  const endISOForResources = safeIsoString(safeLocalDateTime(endsAt));
  const { data: slotResources } = useAvailableResourcesForSlot(
    clinicId,
    serviceId,
    startISOForResources,
    endISOForResources,
    mode === 'edit' ? appointmentId : null,
  );

  const practitionerEligibility = parsePractitionerEligibility(
    selectedService?.practitioner_eligibility,
  );
  const selectedPractitionerIds = (servicePractitioners ?? []).map((row) => row.practitioner_id);
  const flaggedPractitionerMembers = useMemo(() => {
    if (!rolesReady) return null;
    return filterPractitionerMembers(members ?? [], roles ?? []);
  }, [members, roles, rolesReady]);
  const practitionerMembers = useMemo(() => {
    const all = members ?? [];
    if (!flaggedPractitionerMembers) return all;
    const stickyIds = [clinicianId, ...appointmentPractitionerIds];
    if (practitionerEligibility === 'selected') stickyIds.push(...selectedPractitionerIds);
    return withStickySelectedMembers(flaggedPractitionerMembers, all, stickyIds);
  }, [
    members,
    flaggedPractitionerMembers,
    clinicianId,
    appointmentPractitionerIds,
    practitionerEligibility,
    selectedPractitionerIds,
  ]);
  const eligibleMembers = useMemo(() => {
    const filtered = filterPractitionersForService(
      practitionerMembers,
      practitionerEligibility,
      selectedPractitionerIds,
      (member) => member.user_id,
    );
    if (mode !== 'edit') return filtered;
    const missing = appointmentPractitionerIds.flatMap((id) => {
      if (filtered.some((member) => member.user_id === id)) return [];
      const member = members?.find((row) => row.user_id === id);
      return member ? [member] : [];
    });
    return missing.length > 0 ? [...filtered, ...missing] : filtered;
  }, [
    members,
    practitionerMembers,
    practitionerEligibility,
    selectedPractitionerIds,
    appointmentPractitionerIds,
    mode,
  ]);
  const appointmentPractitionerPeople = useMemo(
    () => eligibleMembers.map((member) => ({ id: member.user_id, name: member.name })),
    [eligibleMembers],
  );
  const linkPatientId = patient?.id ?? null;
  const linkedBehandlerIds = useMemo(() => {
    if (!linkPatientId) return [];
    if (!flaggedPractitionerMembers || staffAssignments.isLoading) return null;
    if (staffAssignments.data == null) return staffAssignments.isError ? [] : null;
    return suggestedLinkedPractitionerIds({
      assignmentUserIds: staffAssignments.data.map((row) => row.user_id),
      behandlerIds: new Set(flaggedPractitionerMembers.map((member) => member.user_id)),
    });
  }, [
    flaggedPractitionerMembers,
    linkPatientId,
    staffAssignments.data,
    staffAssignments.isError,
    staffAssignments.isLoading,
  ]);
  const favoritePractitionerIds = useMemo(() => {
    if (linkedBehandlerIds == null) return [];
    const selectable = new Set(appointmentPractitionerPeople.map((person) => person.id));
    return linkedBehandlerIds.filter((id) => selectable.has(id));
  }, [appointmentPractitionerPeople, linkedBehandlerIds]);
  const savedPractitionerIds = useMemo(() => {
    if (mode !== 'edit' || !existing) return [];
    if (practitionerRows.data) {
      return orderedAppointmentPractitionerIds(practitionerRows.data, existing.clinician_id);
    }
    return existing.clinician_id ? [existing.clinician_id] : [];
  }, [mode, existing, practitionerRows.data]);

  const isRecurringSeries =
    existing?.recurrence_pattern !== 'none' &&
    Boolean(existing?.is_recurrence_parent || existing?.recurrence_parent_id);
  const canCancelAppointment = mode === 'edit' && existing?.status === 'upcoming';
  const isTimeBlock =
    Boolean(existing?.is_blocker) || (existing != null && existing.patient_id == null);
  const pastLocked =
    existing != null && !isAppointmentEditable(existing.status, existing.starts_at);
  const openFollowUp = lockedAppointmentOpen({
    isBlock: isTimeBlock,
    hasPatient: Boolean(existing?.patient_id),
    pastLocked,
  });
  const isLockedFromEdits = mode === 'edit' && existing != null && openFollowUp !== 'edit';

  const invoiceLinkQuery = useAppointmentInvoiceLink(
    isLockedFromEdits ? (existing?.id ?? null) : null,
  );

  const redirectedToInvoice = useRef(false);
  useEffect(() => {
    if (!isLockedFromEdits || !existing || invoiceLinkQuery.isLoading) return;
    if (redirectedToInvoice.current) return;
    const action = lockedAppointmentOpen({
      isBlock: isTimeBlock,
      hasPatient: Boolean(existing.patient_id),
      pastLocked: true,
    });
    switch (action) {
      case 'edit':
        return;
      case 'missing_patient':
        redirectedToInvoice.current = true;
        toast.error(t('appointments.invoice.no_patient'));
        onClose();
        return;
      case 'invoice': {
        if (!existing.patient_id) return;
        redirectedToInvoice.current = true;
        onClose();
        navigate(
          buildAppointmentInvoiceHref({
            invoiceId: invoiceLinkQuery.data?.invoice_id,
            patientId: existing.patient_id,
            clinicId: existing.clinic_id,
            appointmentId: existing.id,
            returnTo: `/patients/${existing.patient_id}`,
          }),
        );
        return;
      }
      default: {
        const _exhaustive: never = action;
        return _exhaustive;
      }
    }
  }, [
    isLockedFromEdits,
    isTimeBlock,
    existing,
    invoiceLinkQuery.isLoading,
    invoiceLinkQuery.data,
    navigate,
    onClose,
    t,
  ]);

  // Hydrate state when editing
  useEffect(() => {
    if (mode !== 'edit') return;
    if (isBlockerService || (existing && !existing.patient_id)) setIntent('block');
  }, [mode, isBlockerService, existing]);

  useEffect(() => {
    if (mode !== 'edit' || !existing || practitionerTouched.current) return;
    const ids = practitionerRows.data
      ? orderedAppointmentPractitionerIds(practitionerRows.data, existing.clinician_id)
      : existing.clinician_id
        ? [existing.clinician_id]
        : [];
    setAppointmentPractitionerIds((current) =>
      practitionerSelectionChanged(current, ids) ? [...ids] : current,
    );
  }, [mode, existing, practitionerRows.data]);

  useEffect(() => {
    if (mode !== 'edit' || !existing) return;
    setServiceId(existing.service_id ?? null);
    const existingStart = new Date(existing.starts_at);
    const existingEnd = new Date(existing.ends_at);
    setStartsAt(isoLocal(existingStart));
    setEndsAt(isoLocal(existingEnd));
    setAllDay(
      isAllDayCalendarBlock({
        is_blocker: existing.is_blocker,
        starts_at: existing.starts_at,
        ends_at: existing.ends_at,
      }),
    );
    setSelectedDateTime(existingStart);
    setNotes(existing.notes ?? '');
    setIsVideoMeeting(isVideoAppointment(existing));
  }, [mode, existing]);

  // Hydrate patient when editing — wait for patient data so the name displays correctly
  useEffect(() => {
    if (mode !== 'edit' || !existing || !existing.patient_id || !existingPatient) return;
    setPatient({
      id: existing.patient_id,
      first_name: existingPatient.first_name,
      last_name: existingPatient.last_name,
      email: existingPatient.email,
      phone: primaryTelephoneNumber(existingPatient.telephone_subscribers, existingPatient.phone),
      telephone_subscribers: readTelephoneSubscribers(existingPatient.telephone_subscribers),
    });
  }, [mode, existing, existingPatient]);

  // Ny aftale picks at most one Behandler: the logged-in user when they have
  // the role, otherwise the patient's only linked Behandler. Favorites are
  // display-only and must not be merged into this selection. Bloker tid does
  // not read it — that list still starts from the calendar column.
  useEffect(() => {
    if (mode !== 'create' || practitionerTouched.current) return;
    const next = preselectedAppointmentPractitionerIds({
      loggedInUserId: user?.id,
      userReady: authStatus !== 'initializing',
      members,
      roles,
      rolesReady,
      linkedPractitionerIds: linkedBehandlerIds,
      selectableIds: rolesReady
        ? new Set(appointmentPractitionerPeople.map((person) => person.id))
        : null,
    });
    if (next == null) {
      const waitingOnLinks =
        rolesReady &&
        authStatus !== 'initializing' &&
        members != null &&
        linkedBehandlerIds == null;
      if (!waitingOnLinks) return;
      setAppointmentPractitionerIds((current) => (current.length === 0 ? current : []));
      return;
    }
    setAppointmentPractitionerIds((current) =>
      practitionerSelectionChanged(current, next) ? [...next] : current,
    );
  }, [
    appointmentPractitionerPeople,
    authStatus,
    linkedBehandlerIds,
    members,
    mode,
    roles,
    rolesReady,
    user?.id,
  ]);

  // Initial slot for create mode
  useEffect(() => {
    if (mode !== 'create') return;
    const start = initialStart ?? new Date();
    setStartsAt(isoLocal(start));
    setSelectedDateTime(start);
    const defaultEnd = initialEnd ?? new Date(start.getTime() + 30 * 60_000);
    setEndsAt(isoLocal(defaultEnd));
  }, [mode, initialStart, initialEnd]);

  // Pre-select patient when opened from patient detail
  useEffect(() => {
    if (mode !== 'create' || !initialPatient) return;
    setPatient({
      id: initialPatient.id,
      first_name: initialPatient.first_name,
      last_name: initialPatient.last_name,
      email: initialPatient.email,
      phone: primaryTelephoneNumber(initialPatient.telephone_subscribers, initialPatient.phone),
      telephone_subscribers: readTelephoneSubscribers(initialPatient.telephone_subscribers),
    });
  }, [mode, initialPatient]);

  useEffect(() => {
    if (mode !== 'create' || !initialResourceId || !slotResources?.length) return;
    const match = slotResources.find((opt) => opt.resource_id === initialResourceId);
    if (!match) return;
    setSelectedResourceByGroup((current) =>
      current[match.resource_group_id]
        ? current
        : { ...current, [match.resource_group_id]: match.resource_id },
    );
  }, [mode, initialResourceId, slotResources]);

  useEffect(() => {
    if (practitionerEligibility !== 'selected' || mode === 'edit') return;
    const allowed = new Set(selectedPractitionerIds);
    setAppointmentPractitionerIds((current) => {
      const next = current.filter((id) => allowed.has(id));
      if (next.length === current.length && next.every((id, index) => id === current[index])) {
        return current;
      }
      return next;
    });
  }, [practitionerEligibility, selectedPractitionerIds, mode]);

  // Auto-extend end-time when service duration changes
  function applyServiceDuration(svcId: string | null) {
    setServiceId(svcId);
    setSelectedResourceByGroup({});
    if (!svcId || !startsAt) return;
    const svc = services?.find((s) => s.id === svcId);
    if (!svc) return;
    const startDate = safeLocalDateTime(startsAt);
    if (!startDate) return;
    setEndsAt(isoLocal(appointmentEndForService(startDate, svc.duration_minutes)));
  }

  // Handle availability picker selection
  function handleAvailabilitySelect(dateTime: Date, newClinicianId: string | null) {
    setSelectedDateTime(dateTime);
    setStartsAt(isoLocal(dateTime));

    // Calculate end time based on service duration
    const svc = services?.find((s) => s.id === serviceId);
    const durationMinutes = svc?.duration_minutes ?? 30;
    setEndsAt(isoLocal(appointmentEndForService(dateTime, durationMinutes)));

    if (newClinicianId) {
      setAppointmentPractitionerIds((current) => {
        if (current.includes(newClinicianId)) return current;
        practitionerTouched.current = true;
        return [newClinicianId];
      });
    }
  }

  function chooseAppointmentPractitioners(ids: string[]) {
    practitionerTouched.current = true;
    setAppointmentPractitionerIds(ids);
    setFieldErrors((current) => ({ ...current, clinician: undefined }));
  }

  async function persistAppointmentPractitioners(
    appointment: { id: string; starts_at: string; ends_at: string },
    ids: readonly string[],
    kind: 'create' | 'edit',
  ) {
    if (kind === 'create' && ids.length < 2) return;
    if (kind === 'edit' && !practitionerSelectionChanged(ids, savedPractitionerIds)) return;
    await syncExtraAppointmentPractitioners({
      appointmentId: appointment.id,
      clinicId,
      practitionerIds: ids,
      startsAt: appointment.starts_at,
      endsAt: appointment.ends_at,
    });
  }

  // Get duration for availability picker
  const appointmentDuration = useMemo(() => {
    const svc = services?.find((s) => s.id === serviceId);
    return svc?.duration_minutes ?? 30;
  }, [serviceId, services]);

  const patientOptedOut = Boolean(
    (existingPatient?.appointment_email_opt_out ?? false) ||
    (patient && 'appointment_email_opt_out' in patient
      ? Boolean((patient as { appointment_email_opt_out?: boolean }).appointment_email_opt_out)
      : false),
  );
  const patientHasEmail = Boolean((existingPatient?.email ?? patient?.email ?? '').trim());

  function maybeSendConfirmation(appointmentId: string, paymentStepCompleted: boolean) {
    if (
      !shouldSendStaffAppointmentEmail({
        hasPatient: Boolean(patient),
        appointmentCreated: true,
        paymentStepDeferred: false,
        paymentStepCompleted,
        staffWantsEmail: sendPatientEmail,
        patientOptedOut,
      })
    ) {
      return;
    }
    sendStaffAppointmentConfirmation(appointmentId);
  }

  function maybeSendChangeEmail(appointmentId: string) {
    if (!sendPatientEmail || patientOptedOut || !patientHasEmail) return;
    sendStaffAppointmentEmail(appointmentId, 'rescheduled');
  }

  async function attachVideoMeetings(appointmentIds: string[]) {
    if (!wherebyEnabled || !isVideoMeeting || blockMode) return;
    try {
      for (const id of appointmentIds) {
        await provisionVideo.mutateAsync(id);
      }
    } catch {
      toast.error(t('appointments.video.provision_error'));
    }
  }

  async function syncExistingVideo(appointmentId: string, reason: 'rescheduled' | 'cancelled') {
    if (!wherebyEnabled || blockMode) return;
    if (reason === 'rescheduled' && !isVideoAppointment(existing) && !isVideoMeeting) return;
    if (reason === 'cancelled' && !isVideoAppointment(existing)) return;
    try {
      await syncVideo.mutateAsync({ appointmentId, reason });
    } catch {
      toast.error(t('appointments.video.sync_error'));
    }
  }

  function buildCreateInput(startISO: string, endISO: string): CreateAppointmentInput {
    const resourceIds = Object.values(selectedResourceByGroup).filter(Boolean);
    return {
      clinic_id: clinicId,
      patient_id: patient?.id ?? null,
      starts_at: startISO,
      ends_at: endISO,
      service_id: serviceId,
      clinician_id: clinicianId,
      notes: notes.trim() ? notes : null,
      resource_ids: resourceIds.length > 0 ? resourceIds : null,
    };
  }

  // Re-anchor end-time if user moves the start time
  function applyStart(value: string) {
    if (value && !safeLocalDateTime(value)) return;
    if (blockMode) {
      const nextStart = safeLocalDateTime(value);
      if (!nextStart) {
        setStartsAt(value);
        return;
      }
      const nextEnd = blockEndAfterStartChange({
        previousStart: safeLocalDateTime(startsAt),
        previousEnd: safeLocalDateTime(endsAt),
        nextStart,
      });
      setStartsAt(value);
      setEndsAt(isoLocal(nextEnd));
      setFieldErrors((current) => ({ ...current, end: undefined }));
      return;
    }
    setStartsAt(value);
    if (!value) return;
    const svc = services?.find((s) => s.id === serviceId);
    if (svc) {
      const startDate = safeLocalDateTime(value);
      if (!startDate) return;
      setEndsAt(isoLocal(appointmentEndForService(startDate, svc.duration_minutes)));
    }
  }

  function selectBlockReason(choice: { id: string | null; name: string }) {
    if (choice.id) {
      setServiceId(choice.id);
      setBlockCustomLabel('');
      return;
    }
    setServiceId(null);
    setBlockCustomLabel(choice.name);
  }

  function switchIntent(next: 'appointment' | 'block') {
    setIntent(next);
    if (next === 'block') {
      setBlockClinicianIds((current) =>
        current.length > 0 ? current : clinicianId ? [clinicianId] : [],
      );
      if (!isBlockerService && !serviceId) {
        const first = (services ?? []).find((service) => service.is_blocker);
        if (first) setServiceId(first.id);
      }
      return;
    }
    if (isBlockerService) setServiceId(null);
  }

  async function resolveBlockServiceId(startDate: Date, endDate: Date): Promise<string | null> {
    const minutes = allDay
      ? 60
      : Math.min(
          SERVICE_DURATION_MAX,
          Math.max(15, Math.round((endDate.getTime() - startDate.getTime()) / 60_000)),
        );
    const choice = resolveBlockServiceChoice({
      services: services ?? [],
      selectedId: serviceId,
      customLabel: blockCustomLabel,
      durationMinutes: minutes,
    });
    if (choice.kind === 'missing') {
      toast.error(t('appointments.modal.block_reason_required'));
      return null;
    }
    if (choice.kind === 'existing') return choice.id;
    try {
      const created = await createService.mutateAsync({
        ...DEFAULT_BLOCKER_FORM,
        name: choice.name,
        duration_minutes: choice.durationMinutes,
      });
      return created.id;
    } catch (err) {
      console.error('create block reason failed', err);
      toast.error(t('appointments.modal.block_reason_permission'));
      return null;
    }
  }

  async function persistBlocks(targets: string[]) {
    const startDate = safeLocalDateTime(startsAt);
    const endDate = safeLocalDateTime(endsAt);
    if (!startDate || !endDate || targets.length === 0) return;
    try {
      const resolvedServiceId = await resolveBlockServiceId(startDate, endDate);
      if (!resolvedServiceId) return;
      const multiDay = Boolean(allDay && untilCivil && untilCivil !== startsAt.slice(0, 10));
      const slots = blockCreateSlots({
        clinicianIds: targets,
        allDay,
        startCivil: startsAt.slice(0, 10),
        untilCivil: allDay ? untilCivil || null : null,
        start: startDate,
        end: endDate,
      });
      if (slots.length === 0) {
        toast.error(t('appointments.modal.block_range_invalid'));
        return;
      }
      const repeat = !multiDay && recurrence.pattern !== 'none';
      if (repeat && !recurrence.endDate && !recurrence.count) {
        toast.error(t('appointments.errors.recurrence_end_required'));
        return;
      }
      const createdIds: string[] = [];
      try {
        for (const slot of slots) {
          const slotStartIso = slot.start.toISOString();
          const slotEndIso = slot.end.toISOString();
          if (repeat) {
            const result = await createRecurring.mutateAsync({
              clinic_id: clinicId,
              patient_id: null,
              starts_at: slotStartIso,
              ends_at: slotEndIso,
              service_id: resolvedServiceId,
              clinician_id: slot.clinicianId,
              notes: notes.trim() ? notes : null,
              recurrence_pattern: recurrence.pattern as 'daily' | 'weekly' | 'biweekly' | 'monthly',
              recurrence_end_date: recurrence.endDate || null,
              recurrence_count: recurrence.count || null,
            });
            createdIds.push(...result.map((row) => row.id));
          } else {
            const result = await create.mutateAsync({
              clinic_id: clinicId,
              patient_id: null,
              starts_at: slotStartIso,
              ends_at: slotEndIso,
              service_id: resolvedServiceId,
              clinician_id: slot.clinicianId,
              notes: notes.trim() ? notes : null,
            });
            createdIds.push(result.id);
          }
        }
      } catch (err) {
        for (const id of createdIds) {
          await cancel.mutateAsync({ appointment_id: id }).catch(() => undefined);
        }
        throw err;
      }
      toast.success(t('appointments.modal.block_created', { count: createdIds.length }));
      onClose();
    } catch (err) {
      toast.error(t(appointmentSaveErrorI18nKey(err, false)));
      console.error('block save failed', err);
    }
  }

  async function confirmBlockChoice(choice: 'all' | 'free') {
    const conflicts = blockChoice ?? [];
    const ids = clinicianIdsForBlockSave(blockClinicianIds, conflicts, choice);
    setBlockChoice(null);
    if (ids.length === 0) {
      toast.error(t('appointments.modal.block_multi_none_free'));
      return;
    }
    await persistBlocks(ids);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const nextErrors: { patient?: string; clinician?: string; end?: string } = {};
    const blockTargets =
      blockMode && mode === 'create'
        ? blockClinicianIds.length > 0
          ? blockClinicianIds
          : clinicianId
            ? [clinicianId]
            : []
        : clinicianId
          ? [clinicianId]
          : [];
    if (mode === 'create' && !blockMode && !patient) {
      nextErrors.patient = t('appointments.errors.no_patient');
    }
    if (mode === 'create' && blockTargets.length === 0) {
      nextErrors.clinician = t('appointments.modal.error_clinician');
    }
    const startDate = safeLocalDateTime(startsAt);
    const endDate = safeLocalDateTime(endsAt);
    if (!startDate || !endDate || endDate <= startDate) {
      const startLabel = startDate ? formatAppointmentClock(startDate, i18n.language) : '';
      nextErrors.end = t('appointments.modal.error_end', { start: startLabel });
    }
    setFieldErrors(nextErrors);
    const errorCount = Object.keys(nextErrors).length;
    if (errorCount > 0) {
      if (nextErrors.patient) patientSearchRef.current?.focus();
      else if (nextErrors.clinician) {
        if (blockMode && mode === 'create') blockStaffSearchRef.current?.focus();
        else if (blockMode) clinicianFieldRef.current?.focus();
        else practitionerSearchRef.current?.focus();
      } else endFieldRef.current?.focus();
      return;
    }
    // The search field can hold text before a row is chosen. Create still
    // requires one aftaletype, the same as the old required select.
    if (mode === 'create' && !blockMode && !serviceId) return;
    if (blockClosedDate) {
      toast.error(t('appointments.day_hours.closed_date_cannot_book'));
      return;
    }
    if (!startDate || !endDate) return;
    if (!blockMode && blockOverlaps.length > 0) {
      const confirmed = window.confirm(
        t('appointments.modal.block_overlap_confirm', { label: blockOverlapLabel }),
      );
      if (staffOverlapDecision('block', confirmed) !== 'save') return;
    }
    if (mode === 'create' && blockMode) {
      if (blockOverlapSaveMode(blockTargets.length) === 'choose') {
        if (blockOccupants.isLoading || blockOccupants.isFetching) return;
        if (blockOccupants.isError) {
          toast.error(t('appointments.errors.unknown'));
          return;
        }
        if (blockConflicts.length > 0) {
          setBlockChoice(blockConflicts);
          return;
        }
      } else if (slotHint.kind === 'blocked') {
        toast.error(t('appointments.modal.block_overlap_warning', { label: slotHint.label }));
        return;
      } else if (slotHint.kind === 'practitioner_busy') {
        toast.error(t('appointments.modal.block_on_appointment'));
        return;
      }
      await persistBlocks(blockTargets);
      return;
    }
    if (slotHint.kind === 'practitioner_busy') {
      toast.error(t('appointments.errors.conflict'));
      return;
    }

    const allDayRange = (blockMode || isTimeBlock) && allDay ? allDayBounds(dateValue) : null;
    const startISO = (allDayRange?.start ?? startDate).toISOString();
    const endISO = (allDayRange?.end ?? endDate).toISOString();

    const resourceIds = Object.values(selectedResourceByGroup).filter(Boolean);

    try {
      if (mode === 'create') {
        // Check if recurring
        if (recurrence.pattern !== 'none') {
          // Validate recurrence end condition
          if (!recurrence.endDate && !recurrence.count) {
            toast.error(t('appointments.errors.recurrence_end_required'));
            return;
          }

          const result = await createRecurring.mutateAsync({
            clinic_id: clinicId,
            patient_id: patient?.id ?? null,
            starts_at: startISO,
            ends_at: endISO,
            service_id: serviceId,
            clinician_id: clinicianId,
            notes: notes.trim() ? notes : null,
            recurrence_pattern: recurrence.pattern as 'daily' | 'weekly' | 'biweekly' | 'monthly',
            recurrence_end_date: recurrence.endDate || null,
            recurrence_count: recurrence.count || null,
          });
          try {
            for (const row of result) {
              await persistAppointmentPractitioners(row, appointmentPractitionerIds, 'create');
            }
          } catch (err) {
            for (const row of result) {
              await cancel.mutateAsync({ appointment_id: row.id }).catch(() => undefined);
            }
            throw err;
          }
          toast.success(t('appointments.toasts.series_created', { count: result.length }));
          await attachVideoMeetings(result.map((row) => row.id));
          if (result[0]) maybeSendConfirmation(result[0].id, false);
        } else {
          const result = await create.mutateAsync(buildCreateInput(startISO, endISO));
          try {
            await persistAppointmentPractitioners(result, appointmentPractitionerIds, 'create');
          } catch (err) {
            await cancel.mutateAsync({ appointment_id: result.id }).catch(() => undefined);
            throw err;
          }
          await attachVideoMeetings([result.id]);
          maybeSendConfirmation(result.id, false);
          toast.success(t('appointments.toasts.created'));
        }
      } else if (existing) {
        if (
          lockedAppointmentOpen({
            isBlock: isTimeBlock || blockMode,
            hasPatient: Boolean(existing.patient_id),
            pastLocked: !isAppointmentEditable(existing.status, existing.starts_at),
          }) !== 'edit'
        ) {
          toast.error(t('appointments.errors.past_not_editable'));
          return;
        }
        // Reschedule (always re-sends times even if unchanged)
        const timesChanged =
          startsAt !== isoLocal(new Date(existing.starts_at)) ||
          endsAt !== isoLocal(new Date(existing.ends_at));
        const durationMs = endDate.getTime() - startDate.getTime();
        const deltaMs = startDate.getTime() - new Date(existing.starts_at).getTime();
        if (blockMode && isRecurringSeries && blockScope !== 'single' && timesChanged) {
          const members = await fetchUpcomingSeriesMembers(existing);
          const targets = seriesEditTargets(
            members.map((member) => ({ id: member.id, startsAt: new Date(member.starts_at) })),
            existing.id,
            blockScope,
          );
          for (const target of targets) {
            const shifted = shiftedRange(target.startsAt, deltaMs, durationMs);
            await reschedule.mutateAsync({
              appointment_id: target.id,
              starts_at: shifted.start.toISOString(),
              ends_at: shifted.end.toISOString(),
              clinician_id: clinicianId,
            });
            if (isVideoAppointment(existing) || isVideoMeeting) {
              await syncExistingVideo(target.id, 'rescheduled');
            }
          }
        } else {
          await reschedule.mutateAsync({
            appointment_id: existing.id,
            starts_at: startISO,
            ends_at: endISO,
            clinician_id: clinicianId,
            resource_ids: resourceIds.length > 0 ? resourceIds : null,
          });
          if (!blockMode) {
            await persistAppointmentPractitioners(
              { id: existing.id, starts_at: startISO, ends_at: endISO },
              appointmentPractitionerIds,
              'edit',
            );
          }
        }
        if (timesChanged && !blockMode) {
          await syncExistingVideo(existing.id, 'rescheduled');
          maybeSendChangeEmail(existing.id);
        } else if (!blockMode && isVideoMeeting && !isVideoAppointment(existing)) {
          await attachVideoMeetings([existing.id]);
        }
        if ((existing.notes ?? '') !== notes) {
          if (blockMode && isRecurringSeries && blockScope !== 'single') {
            await updateSeries.mutateAsync({
              appointment_id: existing.id,
              update_future_only: blockScope === 'future',
              notes,
              clinician_id: clinicianId,
            });
          } else {
            await updateNotes.mutateAsync({
              appointment_id: existing.id,
              notes,
            });
          }
        }
        toast.success(
          blockMode ? t('appointments.modal.block_updated') : t('appointments.toasts.updated'),
        );
      }
      onClose();
    } catch (err) {
      toast.error(t(appointmentSaveErrorI18nKey(err, Boolean(existing))));
      console.error('appointment save failed', err);
    }
  }

  function openCancelConfirm() {
    setCancelReason('');
    setCancelScope('single');
    setShowCancelConfirm(true);
  }

  async function onCancelConfirm() {
    if (!existing) return;

    const reason = cancelReason.trim() ? cancelReason.trim() : null;

    try {
      if (blockMode) {
        const plan = blockRemovalPlan({ series: isRecurringSeries, scope: cancelScope });
        switch (plan.action) {
          case 'cancel_series': {
            const count = await deleteSeries.mutateAsync({
              appointment_id: existing.id,
              delete_future_only: plan.futureOnly,
              reason: null,
            });
            toast.success(t('appointments.modal.block_series_removed', { count }));
            break;
          }
          case 'cancel_one': {
            await cancel.mutateAsync({ appointment_id: existing.id, reason: null });
            toast.success(t('appointments.modal.block_removed'));
            break;
          }
          default: {
            const _exhaustive: never = plan;
            throw _exhaustive;
          }
        }
        onClose();
        return;
      }
      if (isRecurringSeries && cancelScope !== 'single') {
        const count = await deleteSeries.mutateAsync({
          appointment_id: existing.id,
          delete_future_only: cancelScope === 'future',
          reason,
        });
        toast.success(t('appointments.toasts.series_deleted', { count }));
      } else {
        await cancel.mutateAsync({ appointment_id: existing.id, reason });
        await syncExistingVideo(existing.id, 'cancelled');
        toast.success(t('appointments.toasts.cancelled'));
        if (sendCancelEmail && !patientOptedOut && patientHasEmail) {
          sendStaffAppointmentEmail(existing.id, 'cancelled');
        }
      }
      onClose();
    } catch (err) {
      console.error('cancel appointment failed', err);
      toast.error(t('appointments.errors.unknown'));
    }
  }

  async function onComplete() {
    if (!existing) return;
    if (!confirm(t('appointments.modal.confirm_complete'))) return;
    try {
      await complete.mutateAsync(existing.id);
      toast.success(t('appointments.toasts.completed'));
      onClose();
    } catch (err) {
      console.error('complete failed', err);
      toast.error(t('appointments.errors.unknown'));
    }
  }

  async function onMarkNoShow() {
    if (!existing) return;
    if (!confirm(t('appointments.modal.confirm_no_show'))) return;
    try {
      await markNoShow.mutateAsync(existing.id);
      toast.success(t('appointments.toasts.no_show'));
      onClose();
    } catch (err) {
      console.error('mark no-show failed', err);
      toast.error(t('appointments.errors.unknown'));
    }
  }

  const pending =
    create.isPending ||
    createService.isPending ||
    createRecurring.isPending ||
    reschedule.isPending ||
    cancel.isPending ||
    complete.isPending ||
    markNoShow.isPending ||
    updateNotes.isPending ||
    deleteSeries.isPending ||
    updateSeries.isPending;

  const patientName = patient ? `${patient.first_name} ${patient.last_name}`.trim() : '';
  const dateValue = startsAt.slice(0, 10);
  const startTime = startsAt.slice(11, 16);
  const endTime = endsAt.slice(11, 16);
  const fieldErrorCount = Object.values(fieldErrors).filter(Boolean).length;
  const createDisabled =
    mode === 'create' &&
    (blockMode ? blockClinicianIds.length === 0 && !clinicianId : !serviceId || !clinicianId);
  const dirty =
    !pending &&
    (mode === 'create'
      ? Boolean(patient || serviceId || notes.trim() || recurrence.pattern !== 'none')
      : existing
        ? notes !== (existing.notes ?? '') ||
          clinicianId !== existing.clinician_id ||
          practitionerSelectionChanged(appointmentPractitionerIds, savedPractitionerIds) ||
          startsAt !== isoLocal(new Date(existing.starts_at)) ||
          endsAt !== isoLocal(new Date(existing.ends_at))
        : false);

  function setCivilDate(value: string) {
    const next = applyAppointmentCivilDate(startsAt, value);
    if (next === startsAt && value !== '' && !isCompleteCivilDate(value)) return;
    if (!isCompleteCivilDate(value)) return;
    applyStart(next);
  }

  function setStartClock(value: string) {
    const date = dateValue || new Date().toISOString().slice(0, 10);
    applyStart(`${date}T${value}`);
  }

  function setEndClock(value: string) {
    const date = dateValue || endsAt.slice(0, 10) || new Date().toISOString().slice(0, 10);
    setEndsAt(`${date}T${value}`);
    setFieldErrors((current) => ({ ...current, end: undefined }));
  }

  const slideTitle = blockMode
    ? mode === 'create'
      ? t('appointments.modal.block_title')
      : t('appointments.modal.block_edit_title')
    : mode === 'create'
      ? t('appointments.modal.new_title')
      : t('appointments.modal.edit_title');
  const descriptionKind = calendarSlideDescription({
    mode,
    block: blockMode || isTimeBlock,
  });
  const slideDescription = (() => {
    switch (descriptionKind) {
      case 'block_create':
        return t('appointments.modal.block_create_description');
      case 'block_edit':
        return t('appointments.modal.block_edit_description');
      case 'appointment_create':
        return t('appointments.modal.create_description', {
          name: patientName || t('appointments.unassigned'),
        });
      case 'appointment_edit':
        return t('appointments.modal.edit_description');
      default: {
        const _exhaustive: never = descriptionKind;
        return _exhaustive;
      }
    }
  })();

  return (
    <>
      <SlideOver
        open
        title={slideTitle}
        description={slideDescription}
        onClose={() => {
          if (pending) return;
          onClose();
        }}
        dirty={dirty}
        footer={
          loadingExisting || isLockedFromEdits ? undefined : (
            <div className="flex w-full flex-wrap items-center gap-s2">
              {createDisabled ? (
                <span id="ny-aftale-disabled-why" className={cn(TYPE.small, 'text-ds-muted')}>
                  {t('appointments.modal.disabled_why')}
                </span>
              ) : null}
              {canCancelAppointment ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={openCancelConfirm}
                  disabled={pending}
                >
                  <CalendarX className="h-4 w-4" />
                  {blockMode
                    ? t('appointments.modal.block_delete')
                    : t('appointments.modal.cancel_appointment')}
                </Button>
              ) : null}
              <div className="ml-auto flex items-center gap-s2">
                <Button type="button" variant="tertiary" onClick={onClose} disabled={pending}>
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  form="appointment-form"
                  disabled={
                    pending ||
                    blockClosedDate ||
                    createDisabled ||
                    (multiBlock && (blockOccupants.isLoading || blockOccupants.isFetching))
                  }
                  loading={pending && mode === 'create'}
                  loadingText={t('appointments.modal.creating')}
                  aria-describedby={createDisabled ? 'ny-aftale-disabled-why' : undefined}
                >
                  {mode === 'create'
                    ? blockMode
                      ? t('appointments.modal.block_save')
                      : t('appointments.modal.save_create')
                    : pending
                      ? t('common.saving')
                      : blockMode
                        ? t('appointments.modal.block_save_update')
                        : t('appointments.modal.save_update')}
                </Button>
              </div>
            </div>
          )
        }
      >
        {loadingExisting || isLockedFromEdits ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
          </div>
        ) : (
          <form id="appointment-form" onSubmit={onSubmit} className="space-y-s4">
            {mode === 'create' ? (
              <div
                className="grid grid-cols-2 gap-s2"
                role="group"
                aria-label={t('appointments.slot_menu.title')}
              >
                <Button
                  type="button"
                  variant={blockMode ? 'secondary' : 'primary'}
                  onClick={() => switchIntent('appointment')}
                >
                  {t('appointments.modal.intent_appointment')}
                </Button>
                <Button
                  type="button"
                  variant={blockMode ? 'primary' : 'secondary'}
                  onClick={() => switchIntent('block')}
                >
                  {t('appointments.modal.intent_block')}
                </Button>
              </div>
            ) : null}

            {fieldErrorCount > 0 ? (
              <Banner
                tone="error"
                title={t('appointments.modal.error_banner', { count: fieldErrorCount })}
              />
            ) : null}
            {pending && mode === 'create' ? (
              <Banner tone="info" title={t('appointments.modal.saving_title')}>
                {t('appointments.modal.saving_body')}
              </Banner>
            ) : null}

            {!blockMode && (
              <PatientPicker
                clinicId={clinicId}
                value={patient}
                onChange={(next) => {
                  setPatient(next);
                  setFieldErrors((current) => ({ ...current, patient: undefined }));
                }}
                disabled={mode === 'edit' || pending}
                error={fieldErrors.patient}
                searchRef={patientSearchRef}
              />
            )}

            {blockMode ? (
              <div className="space-y-s3">
                <p className="type-label text-ds-text">{t('appointments.modal.block_reason')}</p>
                <div className="flex flex-wrap gap-s2">
                  {blockReasonChoices(services ?? []).map((choice) => {
                    const selected =
                      (choice.id != null && choice.id === serviceId && !blockCustomLabel.trim()) ||
                      blockCustomLabel.trim().toLowerCase() === choice.name.trim().toLowerCase();
                    return (
                      <button
                        key={choice.id ?? choice.name}
                        type="button"
                        className={cn(
                          'rounded-full border px-s3 py-s1 text-sm',
                          selected
                            ? 'border-ds-teal bg-ds-teal-tint text-ds-text'
                            : 'border-ds-line bg-ds-card text-ds-text',
                        )}
                        onClick={() => selectBlockReason(choice)}
                      >
                        {choice.name}
                      </button>
                    );
                  })}
                </div>
                <Input
                  id="block-custom-reason"
                  label={t('appointments.modal.block_reason_custom')}
                  value={blockCustomLabel}
                  disabled={pending || mode === 'edit'}
                  placeholder={t('appointments.modal.block_reason_placeholder')}
                  onChange={(event) => setBlockCustomLabel(event.target.value)}
                />
                <p className={cn(TYPE.small, 'text-ds-muted')}>
                  {t('appointments.modal.block_settings_hint')}
                </p>
              </div>
            ) : null}

            {!blockMode && mode === 'create' ? (
              <AppointmentTypeTypeahead
                types={appointmentTypes.map((service) => ({
                  id: service.id,
                  name: service.name,
                  durationMinutes: service.duration_minutes,
                }))}
                value={serviceId}
                onChange={applyServiceDuration}
                label={t('appointments.modal.service')}
                placeholder={t('appointments.modal.service_search_placeholder')}
                hint={t('appointments.modal.service_hint')}
                changeLabel={t('appointments.modal.change_service')}
                emptyLabel={t('appointments.modal.service_search_empty')}
                minutesShort={t('services.minutes_short')}
                disabled={pending}
              />
            ) : null}

            {!blockMode && mode === 'edit' ? (
              <Select
                id="appointment-service"
                label={t('appointments.modal.service')}
                required
                hint={t('appointments.modal.service_hint')}
                value={serviceId ?? ''}
                onChange={(e) => applyServiceDuration(e.target.value || null)}
                disabled
              >
                <option value="">{t('appointments.modal.service_placeholder')}</option>
                {appointmentTypes.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.duration_minutes} {t('services.minutes_short')})
                  </option>
                ))}
              </Select>
            ) : null}

            {blockMode && mode === 'create' ? (
              <>
                <BlockStaffPicker
                  members={members ?? []}
                  selectedIds={blockClinicianIds}
                  disabled={pending}
                  error={fieldErrors.clinician}
                  legend={t('appointments.modal.block_practitioners')}
                  wholeClinicLabel={t('appointments.modal.block_whole_clinic')}
                  searchPlaceholder={t('appointments.modal.block_staff_search')}
                  emptyLabel={t('appointments.modal.block_staff_search_empty')}
                  searchRef={blockStaffSearchRef}
                  onChange={(ids) => {
                    setBlockClinicianIds(ids);
                    setFieldErrors((current) => ({ ...current, clinician: undefined }));
                  }}
                />
                {multiBlock && blockConflicts.length > 0 ? (
                  <Banner tone="warning">
                    {t('appointments.modal.block_multi_overlap_banner', {
                      names: groupBlockConflicts(blockConflicts)
                        .map((group) => group.clinicianName)
                        .join(', '),
                    })}
                  </Banner>
                ) : null}
              </>
            ) : blockMode ? (
              <Select
                id="appointment-clinician"
                ref={clinicianFieldRef}
                label={t('appointments.modal.clinician')}
                required
                error={fieldErrors.clinician}
                value={clinicianId ?? ''}
                onChange={(e) =>
                  chooseAppointmentPractitioners(e.target.value ? [e.target.value] : [])
                }
                disabled={pending}
              >
                <option value="">{t('appointments.modal.clinician_placeholder')}</option>
                {(members ?? []).map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            ) : (
              <PractitionerTypeahead
                people={appointmentPractitionerPeople}
                selectedIds={appointmentPractitionerIds}
                favoriteIds={favoritePractitionerIds}
                onChange={chooseAppointmentPractitioners}
                legend={t('appointments.modal.practitioners')}
                searchPlaceholder={t('appointments.modal.practitioners_search')}
                emptyLabel={t('appointments.modal.practitioners_search_empty')}
                suggestionLabel={t('appointments.modal.practitioners_suggested')}
                error={fieldErrors.clinician}
                inputRef={practitionerSearchRef}
                disabled={pending}
              />
            )}

            {!blockMode && (serviceRequirements ?? []).length > 0 && (
              <div className="space-y-s3">
                <p className="type-label text-ds-text">{t('appointments.modal.resources')}</p>
                {(serviceRequirements ?? []).map((req) => {
                  const groupName =
                    resourceGroups?.find((g) => g.id === req.resource_group_id)?.name ??
                    t('appointments.modal.resource');
                  const options = (slotResources ?? []).filter(
                    (opt) => opt.resource_group_id === req.resource_group_id,
                  );
                  return (
                    <Select
                      key={req.resource_group_id}
                      id={`appointment-resource-${req.resource_group_id}`}
                      label={groupName}
                      value={selectedResourceByGroup[req.resource_group_id] ?? ''}
                      disabled={pending}
                      onChange={(e) =>
                        setSelectedResourceByGroup((current) => ({
                          ...current,
                          [req.resource_group_id]: e.target.value,
                        }))
                      }
                    >
                      <option value="">{t('appointments.modal.resource_auto')}</option>
                      {options.map((opt) => (
                        <option key={opt.resource_id} value={opt.resource_id}>
                          {opt.resource_name}
                          {opt.is_available
                            ? ''
                            : ` (${t('appointments.modal.resource_unavailable')})`}
                        </option>
                      ))}
                    </Select>
                  );
                })}
              </div>
            )}

            <div>
              <div
                className={cn(
                  'mb-s2 flex items-center justify-between gap-s2',
                  blockMode && 'hidden',
                )}
              >
                <span className="type-label text-ds-text">{t('appointments.modal.datetime')}</span>
                <Button
                  type="button"
                  variant="link"
                  disabled={pending || createDisabled}
                  onClick={() => setShowAvailabilityPicker(!showAvailabilityPicker)}
                >
                  {showAvailabilityPicker
                    ? t('appointments.modal.manual_entry')
                    : t('appointments.modal.show_availability')}
                </Button>
              </div>

              {showAvailabilityPicker && !blockMode ? (
                <AvailabilityPicker
                  clinicId={clinicId}
                  serviceId={serviceId}
                  members={eligibleMembers}
                  selectedClinicianId={
                    appointmentPractitionerIds.length === 1 ? appointmentPractitionerIds[0] : null
                  }
                  selectedDateTime={selectedDateTime}
                  duration={appointmentDuration}
                  excludeAppointmentId={mode === 'edit' ? appointmentId : null}
                  onSelect={handleAvailabilitySelect}
                  onClinicianChange={(id) => chooseAppointmentPractitioners(id ? [id] : [])}
                />
              ) : (
                <div className="grid grid-cols-1 gap-s3 nav:grid-cols-3">
                  <DanishDateField
                    name="appointment-date"
                    label={t('appointments.modal.date')}
                    value={isCompleteCivilDate(dateValue) ? dateValue : ''}
                    disabled={pending}
                    required
                    calendar
                    onChange={setCivilDate}
                  />
                  {!allDay ? (
                    <TimeInput
                      id="appointment-start"
                      label={t('appointments.modal.starts_at')}
                      value={startTime}
                      disabled={pending}
                      onChange={setStartClock}
                      required
                    />
                  ) : null}
                  {!allDay ? (
                    <TimeInput
                      id="appointment-end"
                      ref={endFieldRef}
                      label={t('appointments.modal.ends_at')}
                      value={endTime}
                      disabled={pending}
                      error={fieldErrors.end}
                      onChange={setEndClock}
                      required
                    />
                  ) : null}
                </div>
              )}
              {blockMode ? (
                <div className="mt-s3 space-y-s3">
                  <Checkbox
                    id="block-all-day"
                    checked={allDay}
                    disabled={pending}
                    onChange={(event) => {
                      const next = event.target.checked;
                      setAllDay(next);
                      if (!isCompleteCivilDate(dateValue)) return;
                      const bounds = allDayBounds(dateValue);
                      if (!bounds) return;
                      if (!next) {
                        setStartsAt(isoLocal(bounds.start));
                        setEndsAt(isoLocal(new Date(bounds.start.getTime() + 30 * 60_000)));
                        return;
                      }
                      setStartsAt(isoLocal(bounds.start));
                      setEndsAt(isoLocal(bounds.end));
                    }}
                    label={t('appointments.modal.block_all_day')}
                  />
                  {allDay ? (
                    <DanishDateField
                      name="block-until"
                      label={t('appointments.modal.block_until')}
                      value={untilCivil}
                      disabled={pending}
                      calendar
                      onChange={(civil) => {
                        if (civil !== '' && !isCompleteCivilDate(civil)) return;
                        setUntilCivil(civil);
                      }}
                    />
                  ) : null}
                </div>
              ) : null}
              {!fieldErrors.end &&
              !pending &&
              !blockMode &&
              patientName &&
              dateValue &&
              slotHint.kind === 'free' ? (
                <p className={cn(TYPE.small, 'mt-s2 text-ds-muted')}>
                  {t('appointments.modal.slot_hint', { name: patientName })}
                </p>
              ) : null}
              {!blockMode && slotHint.kind === 'patient_other' && patientName ? (
                <p className={cn(TYPE.small, 'mt-s2 text-ds-muted')}>
                  {t('appointments.modal.slot_hint_patient_other', { name: patientName })}
                </p>
              ) : null}
              {slotHint.kind === 'practitioner_busy' ? (
                <Banner tone="warning" className="mt-s2">
                  {t(
                    blockMode
                      ? 'appointments.modal.block_on_appointment'
                      : 'appointments.modal.slot_hint_practitioner_busy',
                    { label: slotHint.label },
                  )}
                </Banner>
              ) : null}
              {blockOverlaps.length > 0 && (
                <Banner tone="warning" className="mt-s2">
                  {t('appointments.modal.block_overlap_warning', { label: blockOverlapLabel })}
                </Banner>
              )}
              {blockClosedDate && (
                <Banner tone="warning" className="mt-s2">
                  {t('appointments.day_hours.closed_date_cannot_book')}
                </Banner>
              )}
              {outsideOpeningHours && !blockClosedDate && (
                <Banner tone="warning" className="mt-s2">
                  {t('appointments.day_hours.outside_hours_warning')}
                </Banner>
              )}
            </div>

            <Textarea
              id="appointment-notes"
              label={t('appointments.modal.notes')}
              hint={t('appointments.modal.notes_hint')}
              value={notes}
              disabled={pending}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={t('appointments.modal.notes_placeholder')}
            />

            {blockMode && mode === 'edit' && isRecurringSeries ? (
              <fieldset className="space-y-s2">
                <legend className="type-label text-ds-text">
                  {t('appointments.modal.block_scope')}
                </legend>
                {(['single', 'future', 'all'] as const).map((scope) => (
                  <label key={scope} className="flex items-center gap-s2 text-sm text-ds-text">
                    <input
                      type="radio"
                      name="block-scope"
                      checked={blockScope === scope}
                      onChange={() => setBlockScope(scope)}
                    />
                    {t(`appointments.modal.block_scope_${scope}`)}
                  </label>
                ))}
              </fieldset>
            ) : null}

            {!blockMode && wherebyEnabled ? (
              <Checkbox
                id="appointment-video-meeting"
                checked={isVideoMeeting}
                disabled={pending}
                onChange={(e) => setIsVideoMeeting(e.target.checked)}
                label={t('appointments.video.checkbox')}
                hint={t('appointments.video.checkbox_hint')}
              />
            ) : null}

            {!blockMode && wherebyEnabled && isVideoAppointment(existing) ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  void (async () => {
                    if (!existing) return;
                    try {
                      const meeting = await joinVideo.mutateAsync(existing.id);
                      if (meeting.hostUrl) openExternalMeeting(meeting.hostUrl);
                    } catch {
                      toast.error(t('appointments.video.join_error'));
                    }
                  })();
                }}
                loading={joinVideo.isPending}
              >
                <Video className="h-4 w-4" aria-hidden />
                {t('appointments.video.join')}
              </Button>
            ) : null}

            {!blockMode && (patient || mode === 'edit') ? (
              <Checkbox
                id="appointment-send-email"
                checked={sendPatientEmail && !patientOptedOut && patientHasEmail}
                disabled={pending || patientOptedOut || !patientHasEmail}
                onChange={(e) => setSendPatientEmail(e.target.checked)}
                label={t('appointments.modal.send_patient_email')}
                hint={
                  patientOptedOut
                    ? t('appointments.modal.send_patient_email_opted_out')
                    : !patientHasEmail
                      ? t('appointments.modal.send_patient_email_no_email')
                      : t('appointments.modal.send_patient_email_hint')
                }
              />
            ) : null}

            {mode === 'create' && !(blockMode && allDay && untilCivil) && (
              <RecurrenceForm
                value={recurrence}
                onChange={setRecurrence}
                disabled={recurrenceFieldDisabled({
                  pending,
                  block: blockMode,
                  hasService: Boolean(serviceId),
                })}
              />
            )}

            {mode === 'edit' && existing?.status === 'upcoming' && !blockMode && (
              <div className="flex flex-wrap gap-s2">
                <Button type="button" variant="secondary" onClick={onMarkNoShow} disabled={pending}>
                  {t('appointments.modal.no_show_appointment')}
                </Button>
                <Button type="button" variant="secondary" onClick={onComplete} disabled={pending}>
                  {t('appointments.modal.complete_appointment')}
                </Button>
              </div>
            )}

            {!blockMode ? (
              <p className={cn(TYPE.small, 'text-ds-muted')}>
                {t('appointments.modal.payment_note')}
              </p>
            ) : null}
          </form>
        )}
      </SlideOver>

      <CancelAppointmentModal
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => void onCancelConfirm()}
        pending={cancel.isPending || deleteSeries.isPending}
        patientName={
          existingPatient
            ? `${existingPatient.first_name} ${existingPatient.last_name}`.trim()
            : patientName
        }
        serviceName={selectedService?.name ?? existing?.service_name}
        startsAt={existing?.starts_at ?? safeLocalDateTime(startsAt)}
        endsAt={existing?.ends_at ?? safeLocalDateTime(endsAt)}
        clinicianName={eligibleMembers.find((m) => m.user_id === clinicianId)?.name}
        isRecurringSeries={isRecurringSeries}
        seriesKind={blockMode ? 'block' : 'appointment'}
        subject={blockMode ? 'block' : 'appointment'}
        cancelScope={cancelScope}
        onCancelScopeChange={setCancelScope}
        reason={cancelReason}
        onReasonChange={setCancelReason}
        sendEmail={blockMode ? undefined : sendCancelEmail}
        onSendEmailChange={blockMode ? undefined : setSendCancelEmail}
        sendEmailDisabledReason={
          patientOptedOut
            ? t('appointments.modal.send_patient_email_opted_out')
            : !patientHasEmail
              ? t('appointments.modal.send_patient_email_no_email')
              : null
        }
      />

      {blockChoice ? (
        <Modal
          tone="warning"
          showIcon
          role="alertdialog"
          title={t('appointments.modal.block_multi_overlap_title')}
          description={t('appointments.modal.block_multi_overlap_body')}
          onClose={() => setBlockChoice(null)}
          footer={
            <>
              <Button type="button" variant="tertiary" onClick={() => setBlockChoice(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void confirmBlockChoice('free')}
              >
                {t('appointments.modal.block_multi_create_free')}
              </Button>
              <Button type="button" onClick={() => void confirmBlockChoice('all')}>
                {t('appointments.modal.block_multi_create_all')}
              </Button>
            </>
          }
        >
          <ul className="max-h-64 space-y-s3 overflow-auto">
            {groupBlockConflicts(blockChoice).map((group) => (
              <li key={group.clinicianId}>
                <p className="font-semibold text-ds-text">{group.clinicianName}</p>
                <ul className={cn(TYPE.small, 'mt-s1 text-ds-text-2')}>
                  {group.items.map((item) => (
                    <li key={`${item.kind}-${item.detail}`}>
                      {t(
                        item.kind === 'appointment'
                          ? 'appointments.modal.block_multi_overlap_appointment'
                          : 'appointments.modal.block_multi_overlap_block',
                        { label: item.detail },
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </Modal>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// PatientPicker — typeahead used inside the modal. When `disabled` is true
// (edit mode), shows the locked patient as plain text.
// ---------------------------------------------------------------------------
function PatientPicker({
  clinicId,
  value,
  onChange,
  disabled,
  error,
  searchRef,
}: {
  clinicId: string;
  value: PatientPickerItem | null;
  onChange: (next: PatientPickerItem | null) => void;
  disabled?: boolean;
  error?: string;
  searchRef?: Ref<HTMLInputElement>;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: results } = usePatientPicker(clinicId, search);
  const list = useMemo(() => results ?? [], [results]);
  const displayName = value ? `${value.first_name} ${value.last_name}`.trim() || value.id : '';
  const phone = value
    ? formatPatientTelephoneNumbers(value.telephone_subscribers, value.phone)
    : '';

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  if (value) {
    return (
      <Field
        label={t('appointments.modal.patient')}
        required
        htmlFor="appointment-patient"
        error={error}
        errorId="appointment-patient-error"
      >
        <div className="flex items-center gap-s3 rounded-pop border border-ds-line bg-ds-bg px-s3 py-s2">
          <Avatar name={displayName} size={36} />
          <div className="min-w-0 flex-1">
            <p className={cn(TYPE.section, 'truncate text-ds-text')}>{displayName}</p>
            {phone ? (
              <p className={cn(TYPE.small, 'mt-0.5 truncate text-ds-muted')}>{phone}</p>
            ) : null}
          </div>
          {!disabled ? (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={() => {
                onChange(null);
                setSearch('');
              }}
            >
              {t('appointments.modal.change_patient')}
            </Button>
          ) : null}
        </div>
      </Field>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        id="appointment-patient"
        ref={searchRef}
        label={t('appointments.modal.patient')}
        required
        error={error}
        leadingIcon={<Search className="h-4 w-4" />}
        value={search}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={t('appointments.modal.patient_search_placeholder')}
      />
      {open && list.length > 0 && (
        <ul className="absolute z-10 mt-s1 max-h-60 w-full overflow-auto rounded-pop border border-ds-line bg-ds-card py-s1 shadow-pop">
          {list.map((p) => {
            const name = `${p.first_name} ${p.last_name}`.trim() || p.id;
            const meta = [p.email, formatPatientTelephoneNumbers(p.telephone_subscribers, p.phone)]
              .filter(Boolean)
              .join(' · ');
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(p);
                    setSearch('');
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-s3 px-s3 py-s2 text-left hover:bg-ds-teal-tint"
                >
                  <Avatar name={name} size={36} />
                  <span className="min-w-0">
                    <span className={cn(TYPE.section, 'block truncate text-ds-text')}>{name}</span>
                    {meta ? (
                      <span className={cn(TYPE.small, 'block truncate text-ds-muted')}>{meta}</span>
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
